import { useEffect, useMemo, useState } from "react";

import AuthScreen from "./components/AuthScreen";
import LoadingScreen from "./components/LoadingScreen";
import OnboardingScreen from "./components/OnboardingScreen";
import TrackerPage from "./pages/TrackerPage";
import SearchPage from "./pages/SearchPage";
import GroupsPage from "./pages/GroupsPage";
import StatsPage from "./pages/StatsPage";
import ProfilePage from "./pages/ProfilePage";
import { useAuth } from "./hooks/useAuth";
import { useEntries } from "./hooks/useEntries";
import { useGroups } from "./hooks/useGroups";
import { useProfile } from "./hooks/useProfile";
import { DEFAULT_TYPE, EMPTY_DRAFT, STATUS_OPTIONS } from "./utils/constants";
import { loaderPageStyle } from "./styles/styles";

function createEmptyDraft(type = DEFAULT_TYPE) {
  return {
    ...EMPTY_DRAFT,
    type,
    status: STATUS_OPTIONS[type][0].label,
  };
}

export default function App() {
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [showForm, setShowForm] = useState(false);
  const [activePage, setActivePage] = useState("tracker");

  const {
    user,
    authLoading,
    authBusy,
    email,
    password,
    confirmPassword,
    authError,
    login,
    signup,
    logout,
    resetAuthForm,
    setEmail,
    setPassword,
    setConfirmPassword,
  } = useAuth();

  const {
    groups,
    groupsLoading,
    activeGroup,
    groupName,
    inviteCode,
    groupError,
    setGroupName,
    setInviteCode,
    handleCreateGroup,
    handleJoinGroup,
    handleLeaveGroup,
    handleSelectGroup,
    handleRenameGroup,
    clearActiveGroup,
  } = useGroups(user);

  const { profile, profileLoading, needsOnboarding, saveProfile } =
    useProfile(user);

  const [draft, setDraft] = useState(() => createEmptyDraft());

  const {
    entries,
    loading: entriesLoading,
    addEntry,
    updateEntry,
    removeEntry,
  } = useEntries(activeGroup, user);

  useEffect(() => {
    if (!user) {
      clearActiveGroup();
    }
  }, [user, clearActiveGroup]);

  const visibleEntries = useMemo(() => {
    return entries
      .filter((entry) => filter === "all" || entry.type === filter)
      .sort((a, b) => {
        if (sortBy === "rating") {
          return (b.rating || 0) - (a.rating || 0);
        }

        if (sortBy === "status") {
          return (a.status || "").localeCompare(b.status || "");
        }

        if (sortBy === "user") {
          return (a.addedBy?.email || "").localeCompare(b.addedBy?.email || "");
        }

        return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
      });
  }, [entries, filter, sortBy]);

  function updateDraft(field, value) {
    setDraft((currentDraft) => ({
      ...currentDraft,
      [field]: value,
    }));
  }

  function openEntrySheet(entry = null) {
    if (entry) {
      setDraft({
        ...createEmptyDraft(entry.type),
        ...entry,
        tags: (entry.tags || []).join(", "),
      });
    } else {
      setDraft(createEmptyDraft());
    }

    setShowForm(true);
  }

  function closeEntrySheet() {
    setShowForm(false);
    setDraft(createEmptyDraft());
  }

  function handleToggleEntrySheet() {
    if (showForm) {
      closeEntrySheet();
      return;
    }

    openEntrySheet();
  }

  async function handleSaveEntry() {
    if (draft.id) {
      await updateEntry(draft);
    } else {
      await addEntry(draft);
    }

    closeEntrySheet();
  }

  // Picking, creating or joining a group drops the user straight into the
  // Tracker for that group — no confirmation step, no manual reopen.
  async function openGroup(group) {
    await handleSelectGroup(group);
    setActivePage("tracker");
  }

  async function createGroupAndOpen() {
    await handleCreateGroup();
    setActivePage("tracker");
  }

  async function joinGroupAndOpen() {
    await handleJoinGroup();
    setActivePage("tracker");
  }

  if (authLoading) {
    return <LoadingScreen loaderPageStyle={loaderPageStyle} />;
  }

  if (!user) {
    return (
      <AuthScreen
        email={email}
        password={password}
        confirmPassword={confirmPassword}
        error={authError}
        busy={authBusy}
        onEmailChange={setEmail}
        onPasswordChange={setPassword}
        onConfirmPasswordChange={setConfirmPassword}
        onLogin={login}
        onSignup={signup}
        onModeChange={resetAuthForm}
      />
    );
  }

  if (profileLoading) {
    return <LoadingScreen loaderPageStyle={loaderPageStyle} />;
  }

  // First run: the account exists in Firebase Auth but has never picked a name
  // or an avatar. Complete the profile before entering the app.
  if (needsOnboarding) {
    return <OnboardingScreen user={user} onSave={saveProfile} />;
  }

  if (entriesLoading) {
    return <LoadingScreen loaderPageStyle={loaderPageStyle} />;
  }

  return activePage === "search" ? (
    <SearchPage
      activeGroup={activeGroup}
      entries={entries}
      loading={entriesLoading}
      showForm={showForm}
      draft={draft}
      currentUser={user}
      onLogout={logout}
      onDraftChange={updateDraft}
      onSaveEntry={handleSaveEntry}
      onCancelEntry={closeEntrySheet}
      onEditEntry={openEntrySheet}
      onRemoveEntry={removeEntry}
      onNavigate={setActivePage}
    />
  ) : activePage === "profile" ? (
    <ProfilePage
      activeGroup={activeGroup}
      groups={groups}
      entries={entries}
      currentUser={user}
      profile={profile}
      onSaveProfile={saveProfile}
      onLogout={logout}
      onNavigate={setActivePage}
    />
  ) : activePage === "stats" ? (
    <StatsPage
      activeGroup={activeGroup}
      groups={groups}
      entries={entries}
      currentUser={user}
      onNavigate={setActivePage}
    />
  ) : activePage === "groups" ? (
    <GroupsPage
      activeGroup={activeGroup}
      currentUser={user}
      groups={groups}
      groupsLoading={groupsLoading}
      groupName={groupName}
      inviteCode={inviteCode}
      error={groupError}
      onSelectGroup={openGroup}
      onGroupNameChange={setGroupName}
      onInviteCodeChange={setInviteCode}
      onCreateGroup={createGroupAndOpen}
      onJoinGroup={joinGroupAndOpen}
      onRenameGroup={handleRenameGroup}
      onLeaveGroup={handleLeaveGroup}
      onNavigate={setActivePage}
    />
  ) : (
    <TrackerPage
      activeGroup={activeGroup}
      entries={visibleEntries}
      allEntries={entries}
      profile={profile}
      filter={filter}
      sortBy={sortBy}
      showForm={showForm}
      draft={draft}
      currentUser={user}
      onLogout={logout}
      onFilterChange={setFilter}
      onSortChange={setSortBy}
      onToggleForm={handleToggleEntrySheet}
      onDraftChange={updateDraft}
      onSaveEntry={handleSaveEntry}
      onCancelEntry={closeEntrySheet}
      onEditEntry={openEntrySheet}
      onRemoveEntry={removeEntry}
      onNavigate={setActivePage}
    />
  );
}
